import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useLoginContext } from '../../context';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers';

import VirtualizedSongRow from './VirtualizedSongRow';
import MultiDragOverlay from './MultiDragOverlay';
import VirtualizedMobileSongsTable from './VirtualizedMobileSongsTable';
import SongRowSkeleton, { SongRowSkeletonList } from './SongRowSkeleton';
import { isSelected, moveTrack, KeyCode } from '../../utils';
import { analyzeSelection, moveRange, calculateInsertBefore, SelectionInfo } from '../../utils/multiDragUtils';
import { ITEMS_PER_PAGE } from '../../constants/common';
import { SongsTableStyle } from './SongsTable.style';

interface VirtualizedSongsTableProps {
  state: any[];
  setState: (songs: any[]) => void;
  selectedSongs: any[];
  bulkAction: boolean;
  setBulkAction: (value: boolean) => void;
  currentPage: number;
  selectSongFunction: (obj: any, id: any, index: number) => void;
  selectSingleSong: (obj: any, id: any, index: number) => void;
  selectMultpleSongFunction?: (arr: any[], idArr: any[]) => void;
  loadMoreTracks?: () => void;
  hasMoreTracks?: boolean;
  onScrollRestore?: (restoreFn: () => void) => void;
  handleModalCloseRestoreScroll?: () => void;
  saveScrollPosition?: () => void;
  restoreScrollPosition?: () => void;
  playlistState?: any;
  menu?: any;
  hideMenu?: boolean;
  fromBrowse?: boolean;
  HideDates?: boolean;
  playlistDispatch?: any;
  onSongsReorder?: (oldIndex: number, newIndex: number, count?: number) => void;
  isSongplaceOnly?: boolean;
  clearSelection?: () => void;
}

const VirtualizedSongsTable: React.FC<VirtualizedSongsTableProps> = ({
  state,
  setState,
  selectedSongs,
  bulkAction,
  setBulkAction,
  currentPage,
  selectSongFunction,
  selectSingleSong,
  selectMultpleSongFunction,
  loadMoreTracks,
  hasMoreTracks,
  playlistState,
  menu,
  hideMenu,
  fromBrowse,
  HideDates,
  playlistDispatch,
  onScrollRestore,
  handleModalCloseRestoreScroll,
  saveScrollPosition,
  restoreScrollPosition,
  onSongsReorder,
  isSongplaceOnly,
  clearSelection,
}) => {
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { playlistId } = useParams();
  const { state: loginState } = useLoginContext();

  // Scroll position preservation for modal restoration
  const savedScrollTop = useRef<number>(0);

  // SearchId for Spotify links - matching original component
  const SearchId = playlistState?.selectedPlaylistId || '';

  // State for multi-selection functionality
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  
  // State for drag overlay functionality
  const [activeDragItem, setActiveDragItem] = useState<any>(null);

  // State for multi-drag selection analysis
  const [selectionInfo, setSelectionInfo] = useState<SelectionInfo>({
    isConsecutive: true,
    startIndex: -1,
    endIndex: -1,
    count: 0,
    selectedIndices: [],
  });

  // Save scroll position before modals open
  const saveScrollPositionInternal = useCallback(() => {
    if (tableContainerRef.current) {
      savedScrollTop.current = tableContainerRef.current.scrollTop || 0;
    }
  }, []);

  // Restore scroll position after modals close
  const restoreScrollPositionInternal = useCallback(() => {
    if (tableContainerRef.current && savedScrollTop.current > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (tableContainerRef.current) {
          tableContainerRef.current.scrollTop = savedScrollTop.current;
        }
      });
    }
  }, []);

  // Provide scroll restoration to parent for modal close handling
  React.useEffect(() => {
    if (onScrollRestore) {
      onScrollRestore(restoreScrollPositionInternal);
    }
  }, [onScrollRestore, restoreScrollPositionInternal]);

  // Use refs to avoid dependency issues with scroll listener
  const hasMoreTracksRef = useRef(hasMoreTracks);
  const loadMoreTracksRef = useRef(loadMoreTracks);

  // Update refs when values change
  useEffect(() => {
    hasMoreTracksRef.current = hasMoreTracks;
  }, [hasMoreTracks]);

  useEffect(() => {
    loadMoreTracksRef.current = loadMoreTracks;
  }, [loadMoreTracks]);

  // Manual scroll listener to fix Virtuoso endReached failure after drag operations
  useEffect(() => {

    const handleScroll = () => {
      if (!tableContainerRef.current) {
        return;
      }

      const container = tableContainerRef.current;
      const isDragging = localStorage.getItem('isDragging') === 'true';

      // Calculate scroll position
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      const isNearBottom = distanceFromBottom < 100;


      if (isNearBottom && hasMoreTracksRef.current && loadMoreTracksRef.current && !isDragging) {
        loadMoreTracksRef.current();
      } else {
        
      }
    };

    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
    } else {
    }

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, []); // Empty dependency array - only run once

  // Handle ResizeObserver errors gracefully without suppressing console
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      if (event.message?.includes('ResizeObserver loop completed')) {
        // Prevent the error from showing in console during known virtualization scenarios
        event.preventDefault();
        return true;
      }
      return false;
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (event.reason?.message?.includes('ResizeObserver loop completed')) {
        event.preventDefault();
        return true;
      }
      return false;
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // Keyboard event listeners for multi-selection (Ctrl and Shift keys)
  useEffect(() => {
    // Clear stale key states on mount (in case of previous stuck state)
    localStorage.setItem('ctrlHold', 'false');
    localStorage.setItem('shiftHold', 'false');

    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.keyCode === KeyCode('ctrl')) {
        localStorage.setItem('ctrlHold', 'true');
      }
      if (ev.keyCode === KeyCode('shift')) {
        localStorage.setItem('shiftHold', 'true');
      }
    };

    const handleKeyUp = (ev: KeyboardEvent) => {
      if (ev.keyCode === KeyCode('ctrl')) {
        localStorage.setItem('ctrlHold', 'false');
      }
      if (ev.keyCode === KeyCode('shift')) {
        localStorage.setItem('shiftHold', 'false');
      }
    };

    // Clear key states when window loses focus (keyup may not fire)
    const handleWindowBlur = () => {
      localStorage.setItem('ctrlHold', 'false');
      localStorage.setItem('shiftHold', 'false');
    };

    // Clear key states when window regains focus (safety reset)
    const handleWindowFocus = () => {
      localStorage.setItem('ctrlHold', 'false');
      localStorage.setItem('shiftHold', 'false');
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  // Analyze selection whenever selectedSongs or state changes
  useEffect(() => {
    const info = analyzeSelection(selectedSongs, state);
    setSelectionInfo(info);
  }, [selectedSongs, state]);

  // Compute whether drag should be allowed based on selection
  const canDrag = useMemo(() => {
    if (!selectedSongs || selectedSongs.length <= 1) return true;
    return selectionInfo.isConsecutive;
  }, [selectedSongs, selectionInfo]);

  // Drag and drop sensors configuration - exact copy from SongsTableNew
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 300,
        tolerance: 5,
      },
    })
  );

  // Multi-selection callback functions - exact copy from SongsTableNew
  const selectSongFn = useCallback(
    (a: any, i: number) => {
      saveScrollPositionInternal();
      selectSongFunction(
        {
          uri: a?.rowNumber?.e?.item.uri,
          positions: [
            a.lockPosition ? Number(a.lockPositionNumber) : i + (currentPage - 1) * ITEMS_PER_PAGE,
          ],
        },
        a._id,
        i
      );
      // Update selectedIndex so Shift+click has a proper anchor point
      setSelectedIndex(i);
      restoreScrollPositionInternal();
    },
    [selectSongFunction, currentPage, saveScrollPositionInternal, restoreScrollPositionInternal]
  );

  const selectSingleSongFn = useCallback(
    (a: any, i: number) => {
      saveScrollPositionInternal();
      selectSingleSong(
        {
          uri: a?.rowNumber?.e?.item.uri,
          positions: [
            a.lockPosition ? Number(a.lockPositionNumber) : i + (currentPage - 1) * ITEMS_PER_PAGE,
          ],
        },
        a._id,
        i
      );
      setSelectedIndex(i);
      restoreScrollPositionInternal();
    },
    [selectSingleSong, currentPage, saveScrollPositionInternal, restoreScrollPositionInternal]
  );

  const rangeSongSelect = useCallback(
    (a: any, toSelect: number) => {
      saveScrollPositionInternal();
      
      // If no anchor point is set, treat the clicked item as a single selection
      const anchorIndex = selectedIndex !== null ? selectedIndex : toSelect;
      
      let idArr = [];
      let arr = [];
      state?.forEach((b, eachSong) => {
        if (
          (eachSong >= anchorIndex && toSelect >= eachSong) ||
          (eachSong <= anchorIndex && toSelect <= eachSong)
        ) {
          if (b._id) {
            idArr.push(b._id);
          }
          arr.push({
            uri: b?.rowNumber?.e?.item.uri,
            positions: [
              b.lockPosition
                ? Number(b.lockPositionNumber)
                : eachSong + (currentPage - 1) * ITEMS_PER_PAGE,
            ],
          });
        }
      });

      if (selectMultpleSongFunction) {
        selectMultpleSongFunction(arr, idArr);
      }
      restoreScrollPositionInternal();
    },
    [state, selectedIndex, currentPage, selectMultpleSongFunction, saveScrollPositionInternal, restoreScrollPositionInternal]
  );


  // Handle drag move event - exact copy from SongsTableNew
  const handleDragMove = useCallback(event => {
    // The modifiers now handle boundary constraints, this is just for additional safety
    if (!tableContainerRef.current) return;
  }, []);

  // Handle drag end event with scroll preservation - supports both single and multi-drag
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;

      // Clear drag state
      localStorage.setItem('isDragging', 'false');
      const dragItem = activeDragItem;
      setActiveDragItem(null);

      // WORKAROUND: Also force load more tracks after drag operations
      setTimeout(() => {
        if (hasMoreTracks && loadMoreTracks && localStorage.getItem('dragTouchedBottom') === 'true') {
          localStorage.setItem('dragTouchedBottom', 'false');
          loadMoreTracks();
        }
      }, 100);

      if (!over || active.id === over.id) return;

      const dropTargetIndex = state.findIndex(
        item => (item.uniqueId || item.id || item._id) === over.id
      );

      if (dropTargetIndex === -1) return;

      // Save scroll position before making changes
      if (saveScrollPosition) {
        saveScrollPosition();
      }

      // Check if this is a multi-drag operation
      if (dragItem?.isMulti && selectionInfo.isConsecutive && selectionInfo.count > 1) {
        // ========== MULTI-DRAG PATH ==========
        const { startIndex, count } = selectionInfo;

        // Calculate new state using moveRange utility
        const newState = moveRange(state, startIndex, count, dropTargetIndex);
        setState(newState);

        // Restore scroll position immediately after state update
        if (restoreScrollPosition) {
          restoreScrollPosition();
        }

        // Calculate Spotify API insert_before parameter
        const insertBefore = calculateInsertBefore(startIndex, count, dropTargetIndex);

        try {
          // Call Spotify API to move the tracks with range_length
          await moveTrack({
            token: loginState.token,
            playlistId: playlistId,
            rangeStart: startIndex,
            insertBefore,
            rangeLength: count,
          });

          // Notify parent of successful reorder to sync raw songs state
          // Pass dropTargetIndex directly - PlaylistNew.tsx handles the adjustment
          if (onSongsReorder) {
            onSongsReorder(startIndex, dropTargetIndex, count);
          }

          // Clear selection after successful multi-drag so user can continue with single drags
          if (clearSelection) {
            clearSelection();
          }

          toast.success(`Moved ${count} songs successfully`);
        } catch (error) {
          console.error('Failed to move tracks:', error);

          // Rollback the optimistic update on error
          setState(state);

          // Show error message to user
          if (error.response?.status === 401) {
            toast.error('Session expired. Please refresh the page and try again.');
          } else {
            toast.error('Failed to move songs. Please try again.');
          }
        }
      } else {
        // ========== SINGLE-DRAG PATH ==========
        const oldIndex = state.findIndex(
          item => (item.uniqueId || item.id || item._id) === active.id
        );
        const newIndex = dropTargetIndex;

        if (oldIndex === -1) return;

        // Optimistically update the UI first
        const newState = arrayMove(state, oldIndex, newIndex);
        setState(newState);

        // Restore scroll position immediately after state update
        if (restoreScrollPosition) {
          restoreScrollPosition();
        }

        // Calculate Spotify API parameters
        const rangeStart = oldIndex;
        const insertBefore = newIndex < oldIndex ? newIndex : newIndex + 1;

        try {
          // Call Spotify API to move the track
          await moveTrack({
            token: loginState.token,
            playlistId: playlistId,
            rangeStart,
            insertBefore,
            rangeLength: 1,
          });

          // Notify parent of successful reorder to sync raw songs state
          if (onSongsReorder) {
            onSongsReorder(oldIndex, newIndex);
          }
        } catch (error) {
          console.error('Failed to move track:', error);

          // Rollback the optimistic update on error
          setState(state);

          // Show error message to user
          if (error.response?.status === 401) {
            toast.error('Session expired. Please refresh the page and try again.');
          } else {
            toast.error('Failed to move song. Please try again.');
          }
        }
      }
    },
    [
      state,
      setState,
      saveScrollPosition,
      restoreScrollPosition,
      loginState.token,
      playlistId,
      hasMoreTracks,
      loadMoreTracks,
      onSongsReorder,
      activeDragItem,
      selectionInfo,
      clearSelection,
    ]
  );

  // Item renderer for Virtuoso
  const ItemComponent = useCallback((index: number) => {
    const song = state[index];

    return (
      <VirtualizedSongRow
        key={song.uniqueId || song.id || song._id}
        index={index}
        song={song}
        selectedSongs={selectedSongs}
        bulkAction={bulkAction}
        setBulkAction={setBulkAction}
        currentPage={currentPage}
        selectSongFunction={selectSongFn}
        selectSingleSong={selectSingleSongFn}
        rangeSongSelect={rangeSongSelect}
        isSelected={isSelected}
        playlistState={playlistState}
        menu={menu}
        hideMenu={hideMenu}
        fromBrowse={fromBrowse}
        HideDates={HideDates}
        playlistDispatch={playlistDispatch}
        SearchId={SearchId}
        onMenuOpen={saveScrollPositionInternal}
        disableDrag={selectedSongs && selectedSongs.length > 1 && !canDrag}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, selectedSongs, bulkAction, setBulkAction, currentPage, selectSongFn, selectSingleSongFn, rangeSongSelect, playlistState, menu, hideMenu, fromBrowse, HideDates, playlistDispatch, SearchId, saveScrollPositionInternal, canDrag]);

  // Footer component for infinite loading
  const Footer = useCallback(() => {
    if (!hasMoreTracks || isSongplaceOnly) return null;

    return <SongRowSkeletonList count={3} />;
  }, [hasMoreTracks, isSongplaceOnly]);

  // Shown instead of real rows while scrolling fast (scrollSeekConfiguration below);
  // without it Virtuoso renders empty boxes that flash black.
  const ScrollSeekPlaceholder = useCallback(
    ({ height }: { height: number }) => <SongRowSkeleton height={height} />,
    []
  );

  // Infinite scroll handler
  const endReached = useCallback(() => {
    const isDragging = localStorage.getItem('isDragging') === 'true';
    if (hasMoreTracks && loadMoreTracks && !isDragging) {
      loadMoreTracks();
    } else {
      localStorage.setItem('dragTouchedBottom', 'true');
    }
  }, [hasMoreTracks, loadMoreTracks]);

  // Drag start handler - handles both single and multi-drag
  const handleDragStart = useCallback((event: DragStartEvent) => {
    // Check if trying to drag with non-consecutive selection
    if (selectedSongs && selectedSongs.length > 1 && !selectionInfo.isConsecutive) {
      toast.warning(
        'Drag-and-drop only works for consecutive songs. Please select songs in a continuous range (e.g., using Shift+Click).'
      );
      return;
    }

    localStorage.setItem('isDragging', 'true');

    // Check if this is a multi-drag scenario
    if (selectedSongs && selectedSongs.length > 1 && selectionInfo.isConsecutive) {
      // Multi-drag: store all selected items
      const selectedItems = state.slice(
        selectionInfo.startIndex,
        selectionInfo.endIndex + 1
      );
      setActiveDragItem({
        isMulti: true,
        items: selectedItems,
        ...selectedItems[0],
      });
    } else {
      // Single-drag: store just the dragged item
      const draggedSong = state.find(
        item => (item.uniqueId || item.id || item._id) === event.active.id
      );
      setActiveDragItem({ isMulti: false, ...draggedSong });
    }
  }, [state, selectedSongs, selectionInfo]);

  // DragOverlay component that renders the dragged item
  const DragOverlayComponent = useCallback(() => {
    if (!activeDragItem) return null;

    // Find the index of the dragged item for proper rendering
    const draggedIndex = state.findIndex(
      item => (item.uniqueId || item.id || item._id) === (activeDragItem.uniqueId || activeDragItem.id || activeDragItem._id)
    );

    return (
      <table 
        style={{
          width: '100%',
          tableLayout: 'fixed',
          borderCollapse: 'collapse',
          backgroundColor: 'var(--background-color, #1a1a1a)',
          border: '1px solid var(--border-color, #333)',
          borderRadius: '4px',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
        }}
        className="tablePlayList"
      >
        <tbody>
          <tr className="rowTable" style={{ opacity: 0.9 }}>
            {/* Song Number Column */}
            <td style={{ width: '63px', textAlign: 'center' }}>
              {fromBrowse ? (
                draggedIndex + 1
              ) : (
                <>
                  {playlistState?.previewSong?.id === activeDragItem?.id ? (
                    <div style={{ padding: '6px 0px 0px 0px ' }}>
                      <span style={{ marginLeft: '15px', color: '#1db954' }}>♪</span>
                    </div>
                  ) : activeDragItem?.key ? (
                    activeDragItem?.key
                  ) : (
                    <div
                      className="playButtonPlayList"
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      {activeDragItem.previewUrl ? (
                        <span style={{ color: 'white' }}>▶</span>
                      ) : (
                        <img
                          alt="spotifylogo"
                          src={'./../static/images/spotifyLogo.png'}
                          width={28}
                          height={28}
                          className="icons1"
                        />
                      )}
                      <span className="icons2" style={{ width: '' }}>
                        {draggedIndex + 1}
                      </span>
                    </div>
                  )}
                </>
              )}
            </td>

            {/* Title & Artist Column */}
            <td className="handle">
              <div style={{ cursor: 'grabbing', height: '100%', display: 'flex', alignItems: 'center' }}>
                {typeof activeDragItem?.image === 'string' ? (
                  <div className="imageName">
                    <span className="imgSpan">
                      <span id="hancker">
                        <img
                          className="table-list-image"
                          alt="thumbnail"
                          src={activeDragItem?.image}
                          width="40"
                          height="40"
                        />
                      </span>
                    </span>
                    <div className="namePlaylist">
                      <>
                        <p id="hancker">
                          {activeDragItem?.title?.length > 23 
                            ? activeDragItem?.title.substring(0, 23) + '...' 
                            : activeDragItem?.title}
                        </p>
                        <p id="hancker">
                          {activeDragItem?.artist?.length > 18 
                            ? activeDragItem?.artist.substring(0, 18) + '...' 
                            : activeDragItem?.artist}
                        </p>
                      </>
                    </div>
                  </div>
                ) : (
                  <div className="imageName">
                    <span className="imgSpan">{activeDragItem?.image}</span>
                    <div className="namePlaylist">{activeDragItem?.name}</div>
                  </div>
                )}
              </div>
            </td>

            {/* Popularity Column */}
            <td style={{ textAlign: 'center', position: 'relative' }}>
              {activeDragItem?.popularity ?? '--'}
            </td>

            {/* Dates Column */}
            {HideDates ? null : (
              <td>
                <table className="startEndDate" style={{ width: '220px' }}>
                  <tbody>
                    <tr>
                      <td>
                        <p>Start date:</p>
                      </td>
                      <td>
                        <h3 className="startEndTime">{activeDragItem?.startTime}</h3>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p>End date:</p>
                      </td>
                      <td>
                        <h3 className="startEndTime">
                          {activeDragItem?.endTime ? activeDragItem?.endTime : '-'}
                        </h3>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            )}

            {/* Spotify Column */}
            <td>
              <a
                className="playSpot"
                target="blank"
                href={'https://open.spotify.com/playlist/' + SearchId}
              >
                <img alt="spotifylogo" src={'./../static/images/spotifyLogo.png'} />
                <p style={{ cursor: 'pointer' }}>Play on Spotify</p>
              </a>
            </td>

            {/* Actions Column */}
            <td className="actionsColumn">
              {!hideMenu && menu ? (
                <svg
                  cursor={'pointer'}
                  stroke="currentColor"
                  fill="none"
                  strokeWidth="0"
                  viewBox="0 0 24 24"
                  height="26px"
                  width="26px"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M2 5H14V7H2V5Z" fill="currentColor"></path>
                  <path d="M2 9H14V11H2V9Z" fill="currentColor"></path>
                  <path d="M10 13H2V15H10V13Z" fill="currentColor"></path>
                  <path
                    d="M16 9H18V13H22V15H18V19H16V15H12V13H16V9Z"
                    fill="currentColor"
                  ></path>
                </svg>
              ) : activeDragItem?.Edit ? (
                <>{activeDragItem?.Edit({ ...activeDragItem, latestindex: draggedIndex }) || null}</>
              ) : (
                <></>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }, [activeDragItem, state, fromBrowse, playlistState?.previewSong?.id, HideDates, SearchId, hideMenu, menu]);

  return (
    <SongsTableStyle>
      <div 
        ref={tableContainerRef}
        className="tablePlayListDiv tablePlayListDivMaxHeight" 
        style={{
          position: 'relative',
          overflow: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        {/* Table Header - using proper table structure like original */}
        <table className="tablePlayList">
          <thead>
            <tr>
              <th className="seriesNumber" style={{ width: '63px', textAlign: 'center' }}>
                #
              </th>
              <th style={{ width: '22%' }}>Title / Artist</th>
              <th style={{ textAlign: 'center' }}>
                Popularity
              </th>
              {HideDates ? null : <th>Start date / End date</th>}
              <th></th>
              <th style={{ textAlign: 'right', padding: '0px 20px' }}>
                Actions
              </th>
            </tr>
          </thead>
        </table>

      {/* Virtualized Table Body */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToFirstScrollableAncestor, restrictToVerticalAxis]}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        autoScroll={{
          enabled: true,
          acceleration: 10,
        }}
      >
        <SortableContext
          items={state.map(item => item.uniqueId || item.id || item._id)}
          strategy={verticalListSortingStrategy}
        >
          <table className="tablePlayList" style={{ tableLayout: 'fixed' }}>
            <tbody>
              <tr style={{ visibility: 'hidden', height: '0' }}>
                <td style={{ width: '63px' }}></td>
                <td></td>
                <td style={{ width: '80px' }}></td>
                {HideDates ? null : <td style={{ width: '200px' }}></td>}
                <td style={{ width: '120px' }}></td>
                <td style={{ width: '120px' }}></td>
              </tr>
            </tbody>
          </table>
          <div 
            className="virtualized-table-body"
            style={{
              display: 'block',
              width: '100%',
            }}
          >
            <Virtuoso
              // key={virtuosoKey}
              ref={virtuosoRef}
              className="virtualized-table-body-content"
              totalCount={state.length}
              itemContent={ItemComponent}
              endReached={endReached}
              components={{ Footer, ScrollSeekPlaceholder }}
              overscan={5}
              increaseViewportBy={{ top: 400, bottom: 800 }}
              scrollSeekConfiguration={{
                enter: velocity => Math.abs(velocity) > 200,
                exit: velocity => Math.abs(velocity) < 30,
              }}
              // Reduce re-renders during drag
              computeItemKey={(index) => state[index]?.uniqueId || state[index]?.id || state[index]?._id || index}
            />
          </div>
        </SortableContext>
        <DragOverlay>
          {activeDragItem?.isMulti ? (
            <MultiDragOverlay
              songs={activeDragItem.items}
              count={selectionInfo.count}
            />
          ) : (
            <DragOverlayComponent />
          )}
        </DragOverlay>
      </DndContext>
      </div>

      {/* Mobile Table - Virtualized */}
      <VirtualizedMobileSongsTable
        state={state}
        setState={setState}
        selectedSongs={selectedSongs}
        bulkAction={bulkAction}
        setBulkAction={setBulkAction}
        currentPage={currentPage}
        selectSongFunction={selectSongFunction}
        selectSingleSong={selectSingleSong}
        selectMultpleSongFunction={selectMultpleSongFunction}
        loadMoreTracks={loadMoreTracks}
        hasMoreTracks={hasMoreTracks}
        playlistState={playlistState}
        menu={menu}
        hideMenu={hideMenu}
        HideDates={HideDates}
        playlistDispatch={playlistDispatch}
        onSongsReorder={onSongsReorder}
        isSongplaceOnly={isSongplaceOnly}
        clearSelection={clearSelection}
      />
    </SongsTableStyle>
  );
};

export default VirtualizedSongsTable;
